import { Tabs } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { Form, FormField } from '@/Core/UI/Form'
import { auditValuesOf } from '@/Ship/Components/Detail/DetailShell/DetailShell'
import { ServiceInfo } from '@/Ship/Components/Detail/ServiceInfo/ServiceInfo'
import { ViewRow } from '@/Ship/Components/Detail/ViewRow/ViewRow'
import { FormSection } from '@/Ship/Components/Form/FormSection'
import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { TabLabel } from '@/Ship/Components/UI/TabLabel/TabLabel'

import { MileageControl } from '../Components/MileageControl'
import { ShiftAssignments } from '../Components/ShiftAssignments'
import { type ShiftPhoto, ShiftPhotos } from '../Components/ShiftPhotos'
import { ShiftStatusHistory } from '../Components/ShiftStatusHistory'
import { SHIFT_STATUS_OPTIONS } from '../Contracts/ShiftStatus'
import { formatDuration, formatKm } from '../Helpers/shiftFormat'
import { ShiftAssignmentsStore } from '../Stores/ShiftAssignmentsStore'

const MAIN = ['code', 'shiftDate', 'driver', 'vehicle', 'cluster', 'region', 'externalCode']
const START = ['startedAt', 'startOdometer', 'documentsConfirmed', 'safetyBriefingConfirmed']
const COMPLETE = ['completedAt', 'endOdometer', 'technicalRemarks']

/**
 * Картка зміни (перегляд) — вкладки, як у заявці: «Інформація» (код і бейдж статусу, основні
 * дані, після відповідних дій — «Початок зміни» і «Завершення зміни» з розрахунковими полями
 * та фото несправностей, «Контроль пробігу» (одометр проти Wialon), унизу — «Службова інформація»), «Задачі» з лічильником, «Історія статусів».
 * Службову інформацію шаблон показує сам лише у вкладці «Інформація» (ресурс ховає блок
 * DetailShell у перегляді — hideServiceInfoInView).
 */
export const ShiftViewTemplate = observer(({ crudContext, formContext }: any) => {
    const fields = crudContext?.resource?.fields ?? {}
    const values = formContext?.values ?? {}
    const status = values.status
    const uuid = values.uuid ? String(values.uuid) : ''

    const [assignments] = useState(() => new ShiftAssignmentsStore())
    useEffect(() => {
        assignments.load(uuid)
    }, [assignments, uuid])

    const render = (names: string[]) =>
        names
            // Код ERP — лише для змін з ERP
            .filter((name) => fields[name] && (name !== 'externalCode' || !!values.externalCode))
            .map((name) => <FormField key={name} name={name} />)

    const statusOption = SHIFT_STATUS_OPTIONS.find((option) => option.value === status)

    const info = (
        <Form formContext={formContext}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>{values.code ? `Зміна ${values.code}` : 'Зміна'}</span>
                <StatusBadge option={statusOption} fallbackLabel={status || '—'} />
            </div>
            <FormSection title="Зміна">{render(MAIN)}</FormSection>
            {(status === 'started' || status === 'completed') && (
                <FormSection title="Початок зміни">{render(START)}</FormSection>
            )}
            {status === 'completed' && (
                <FormSection title="Завершення зміни">
                    {render(COMPLETE)}
                    {/* Розрахункові значення — з одиницями, тому не через FormField */}
                    <ViewRow label="Загальна тривалість зміни">{formatDuration(values.durationMinutes)}</ViewRow>
                    <ViewRow label="Загальний пробіг">{formatKm(values.mileage)}</ViewRow>
                    <ViewRow label="Фото несправностей">
                        <ShiftPhotos
                            shiftUuid={uuid}
                            photos={Array.isArray(values.photos) ? (values.photos as ShiftPhoto[]) : []}
                            canManage={!!values._permissions?.photos}
                            onChanged={() => crudContext?.actions?.reloadData?.()}
                        />
                    </ViewRow>
                </FormSection>
            )}
            {status === 'completed' && (
                <FormSection title="Контроль пробігу">
                    <MileageControl
                        shiftUuid={uuid}
                        values={values}
                        canReview={!!values._permissions?.mileageReview}
                        canFetchWialon={!!values._permissions?.wialonMileageFetch}
                        onReviewed={() => crudContext?.actions?.reloadData?.()}
                    />
                </FormSection>
            )}
        </Form>
    )

    // Блок — поза формою, як у DetailShell: інакше остання секція не :last-child і зберігає
    // нижній відступ — зазор перед блоком більший, ніж у довідниках
    const infoTab = (
        <>
            {info}
            <ServiceInfo values={auditValuesOf(formContext?.values)} />
        </>
    )

    if (!uuid) return infoTab

    return (
        <Tabs
            items={[
                { key: 'info', label: 'Інформація', children: infoTab },
                {
                    key: 'assignments',
                    label: <TabLabel count={assignments.total}>Задачі</TabLabel>,
                    children: <ShiftAssignments store={assignments} />,
                },
                {
                    key: 'statusHistory',
                    label: 'Історія статусів',
                    children: <ShiftStatusHistory shiftUuid={uuid} status={status} />,
                },
            ]}
        />
    )
})
